import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircleIcon, WarningIcon, WrenchIcon, ArrowUpIcon, TrashIcon } from "@phosphor-icons/react";

/**
 * v6.5.62 — Bridge & cycle health tile.
 * Reads the public /api/bridge/health endpoint (no auth required so the
 * operator can check from mobile) and renders collection row counts +
 * recent cycles with the number of bots each cycle actually backtested.
 *
 * Answers two questions at a glance:
 *   1. Is the bridge filling every collection it should? (green if growth_24h > 0)
 *   2. Are cycles doing real work, or ghost-cycling? (bots_backtested per cycle)
 */
export default function BridgeHealthTile() {
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [sweepMsg, setSweepMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/bridge/health")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setErr(e?.response?.status === 404 ? "endpoint missing (deploy needed)" : "health unavailable"));
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const runSweep = async () => {
    setSweeping(true); setSweepMsg(null);
    try {
      const r = await api.get("/bridge/dead-configs-sweep?min_months=3");
      const d = r.data || {};
      setSweepMsg(`Added ${d.new_dead_configs || 0} new dead configs · total ${d.total_dead_configs || 0}`);
      // Refresh the health data so the tile shows the new count
      const h = await api.get("/bridge/health");
      setData(h.data);
    } catch (e) {
      setSweepMsg(`Sweep failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSweeping(false);
    }
  };

  if (err) {
    return (
      <div data-testid="bridge-health-tile" className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
        <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Bridge & Cycle Health</div>
        <div className="mono text-sm text-[#FF4D4D] mt-3">{err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid="bridge-health-tile" className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
        <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Bridge & Cycle Health</div>
        <div className="mono text-sm text-[#94A3B8] mt-3">Loading…</div>
      </div>
    );
  }

  const collections = data.collections || {};
  const cycles      = data.recent_cycles || [];
  // Freshness = highest growth_24h across the priority collections.
  const anyGrowth = ["live_monthly_results", "backtest_attempts", "live_bots"]
    .reduce((n, k) => n + (collections[k]?.growth_24h || 0), 0);
  const status = anyGrowth > 0
    ? { icon: CheckCircleIcon, color: "#2ECA7F", label: `Bridge active · ${anyGrowth.toLocaleString()} new rows in 24h` }
    : { icon: WarningIcon,     color: "#FBBF24", label: "No new backtest data in 24h" };
  const Icon = status.icon;

  const rowOrder = [
    "live_bots",
    "live_monthly_results",
    "backtest_attempts",
    "brain_generated_strategies",
    "dead_configs",
    "live_outcomes",
    "live_decisions",
    "live_proposals",
  ];

  return (
    <div data-testid="bridge-health-tile" className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4 space-y-4">
      {/* Header + status */}
      <div>
        <div className="flex items-center gap-2">
          <Icon size={16} color={status.color} weight="fill" />
          <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">
            Bridge & Cycle Health
          </div>
        </div>
        <div className="mono text-lg mt-2" style={{ color: status.color }}>
          {status.label}
        </div>
      </div>

      {/* Collections grid */}
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">
          Collections
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {rowOrder.map((k) => {
            const c = collections[k];
            if (!c) return null;
            const g = c.growth_24h || 0;
            return (
              <div key={k} className="flex items-center justify-between border border-[#2A2D3A] rounded px-3 py-2 bg-[#0F1116]"
                   data-testid={`collection-row-${k}`}>
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">{k}</div>
                  <div className="text-sm text-[#F1F5F9] mt-0.5">{(c.total || 0).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-1 mono text-[11px]"
                     style={{ color: g > 0 ? "#2ECA7F" : "#64748B" }}>
                  {g > 0 && <ArrowUpIcon size={10} weight="bold" />}
                  <span>+{g.toLocaleString()} / 24h</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent cycles */}
      {cycles.length > 0 && (
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">
            Recent cycles
          </div>
          <div className="border border-[#2A2D3A] rounded overflow-hidden">
            <table className="w-full text-[11px] mono">
              <thead className="bg-[#0F1116] text-[#64748B]">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Duration</th>
                  <th className="text-left px-3 py-2">Bots backtested</th>
                  <th className="text-left px-3 py-2">Result</th>
                </tr>
              </thead>
              <tbody>
                {cycles.map((c, i) => {
                  const dur = Number(c.duration_min || 0);
                  const bots = Number(c.bots_backtested || 0);
                  const ghost = dur > 0 && dur < 20 && bots === 0;
                  return (
                    <tr key={i} className={i % 2 ? "bg-[#0F1116]" : ""}
                        data-testid={`cycle-row-${c.cycle}`}>
                      <td className="px-3 py-2 text-[#F1F5F9]">{c.cycle}</td>
                      <td className="px-3 py-2 text-[#F1F5F9]">{dur.toFixed(1)} min</td>
                      <td className="px-3 py-2"
                          style={{ color: bots > 0 ? "#2ECA7F" : ghost ? "#FF4D4D" : "#94A3B8" }}>
                        {bots.toLocaleString()}{ghost ? " · ghost" : ""}
                      </td>
                      <td className="px-3 py-2"
                          style={{ color: c.result === "OK" ? "#2ECA7F" : "#FBBF24" }}>
                        {c.result}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mono text-[10px] text-[#475569] leading-relaxed">
        Ghost cycle = under 20 min &amp; 0 bots backtested. Persistent ghosts mean
        the pipeline is skipping through zombie configs.
      </div>

      {/* Sweep-now button */}
      <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#2A2D3A]">
        <div className="mono text-[10px] text-[#94A3B8] leading-relaxed flex-1">
          Run the dead-config sweep now to fingerprint every zombie bot. The
          brain will refuse to re-propose them next cycle.
        </div>
        <button
          onClick={runSweep}
          disabled={sweeping}
          data-testid="run-dead-config-sweep"
          className="mono text-[10px] uppercase tracking-widest px-3 py-2 rounded border border-[#FF4D4D]/50 bg-[#FF4D4D]/10 text-[#FF4D4D] hover:bg-[#FF4D4D]/20 transition-colors disabled:opacity-50 disabled:cursor-wait flex items-center gap-1.5 shrink-0"
        >
          <TrashIcon size={11} weight="bold" />
          {sweeping ? "Sweeping…" : "Sweep zombies"}
        </button>
      </div>
      {sweepMsg && (
        <div className="mono text-[10px] text-[#F1F5F9]" data-testid="sweep-result">
          {sweepMsg}
        </div>
      )}
    </div>
  );
}
